import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Save,
  Loader2,
  Edit3,
  X,
  User,
  GraduationCap,
  FileText,
  Target,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Building,
  BookOpen,
  Award,
  DollarSign,
  Briefcase,
  Globe
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

const EDUCATION_LEVELS = [
  { value: 'high_school', label: 'High School' },
  { value: 'bachelor', label: "Bachelor's Degree" },
  { value: 'master', label: "Master's Degree" },
  { value: 'phd', label: 'PhD' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'other', label: 'Other' }
];

const INDUSTRIES = [
  { value: 'tech', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other', label: 'Other' }
];

const WORK_LOCATIONS = [
  { value: 'home-country', label: 'Home Country' },
  { value: 'study-country', label: 'Study Country' },
  { value: 'global', label: 'Global/Remote' },
  { value: 'other', label: 'Other' }
];

const ViewField = ({ icon: Icon, label, value, className = '' }) => (
  <div className={`flex items-start gap-3 ${className}`}>
    <div className="p-2 bg-stone-50 rounded-lg">
      <Icon className="w-4 h-4 text-gray-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900 truncate">
        {value || <span className="text-gray-400 italic">Not provided</span>}
      </p>
    </div>
  </div>
);

const EditField = ({ label, required, error, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

export default function AdminStudentPersonalForm({ student, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    age: '',
    institution: '',
    currentEducationLevel: '',
    fieldOfStudy: '',
    gpa: '',
    graduationYear: '',
    ielts: '',
    toefl: '',
    gre: '',
    preferredDegreeLevel: '',
    budget: '',
    careerGoals: '',
    industry: '',
    workLocation: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (student) {
      setFormData({
        firstName: student.firstName || student.user?.firstName || '',
        lastName: student.lastName || student.user?.lastName || '',
        phone: student.phone || student.user?.phone || '',
        country: student.country || student.user?.country || '',
        age: student.age || '',
        institution: student.institution || '',
        currentEducationLevel: student.currentEducationLevel || '',
        fieldOfStudy: student.fieldOfStudy || '',
        gpa: student.gpa || '',
        graduationYear: student.graduationYear || '',
        ielts: student.ielts || '',
        toefl: student.toefl || '',
        gre: student.gre || '',
        preferredDegreeLevel: student.preferredDegreeLevel || '',
        budget: student.budget || '',
        careerGoals: student.careerGoals || '',
        industry: student.industry || '',
        workLocation: student.workLocation || ''
      });
    }
  }, [student]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.updateStudent(student.studentId, formData);
      toast.success('Profile updated successfully');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (student) {
      setFormData({
        firstName: student.firstName || '',
        lastName: student.lastName || '',
        phone: student.phone || '',
        country: student.country || '',
        age: student.age || '',
        institution: student.institution || '',
        currentEducationLevel: student.currentEducationLevel || '',
        fieldOfStudy: student.fieldOfStudy || '',
        gpa: student.gpa || '',
        graduationYear: student.graduationYear || '',
        ielts: student.ielts || '',
        toefl: student.toefl || '',
        gre: student.gre || '',
        preferredDegreeLevel: student.preferredDegreeLevel || '',
        budget: student.budget || '',
        careerGoals: student.careerGoals || '',
        industry: student.industry || '',
        workLocation: student.workLocation || ''
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  const getEducationLabel = (value) => {
    const level = EDUCATION_LEVELS.find(l => l.value === value);
    return level ? level.label : value;
  };

  const getIndustryLabel = (value) => {
    const ind = INDUSTRIES.find(i => i.value === value);
    return ind ? ind.label : value;
  };

  const getWorkLocationLabel = (value) => {
    const loc = WORK_LOCATIONS.find(l => l.value === value);
    return loc ? loc.label : value;
  };

  if (!isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
            <p className="text-sm text-gray-500">View and manage student profile details</p>
          </div>
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300"
          >
            <Edit3 className="w-4 h-4" />
            Edit Profile
          </Button>
        </div>

        <Card className="p-6 border border-stone-100 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="p-2 bg-blue-50 rounded-lg">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <h4 className="font-semibold text-gray-900">Personal Details</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ViewField icon={User} label="Full Name" value={`${formData.firstName} ${formData.lastName}`.trim()} />
            <ViewField icon={Mail} label="Email" value={student?.email || student?.user?.email} />
            <ViewField icon={Phone} label="Phone" value={formData.phone} />
            <ViewField icon={MapPin} label="Country" value={formData.country} />
            <ViewField icon={Calendar} label="Age" value={formData.age ? `${formData.age} years` : null} />
          </div>
        </Card>

        <Card className="p-6 border border-stone-100 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="p-2 bg-purple-50 rounded-lg">
              <GraduationCap className="w-5 h-5 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900">Academic Information</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ViewField icon={Building} label="Institution" value={formData.institution} />
            <ViewField icon={BookOpen} label="Education Level" value={getEducationLabel(formData.currentEducationLevel)} />
            <ViewField icon={Award} label="Field of Study" value={formData.fieldOfStudy} />
            <ViewField icon={FileText} label="GPA" value={formData.gpa} />
            <ViewField icon={Calendar} label="Graduation Year" value={formData.graduationYear} />
            <ViewField icon={GraduationCap} label="Preferred Degree" value={getEducationLabel(formData.preferredDegreeLevel)} />
          </div>
        </Card>

        <Card className="p-6 border border-stone-100 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="p-2 bg-amber-50 rounded-lg">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <h4 className="font-semibold text-gray-900">Test Scores</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-100">
              <p className="text-xs text-blue-600 font-medium mb-1">IELTS</p>
              <p className="text-2xl font-bold text-blue-700">
                {formData.ielts || <span className="text-gray-300 text-lg">--</span>}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl border border-green-100">
              <p className="text-xs text-green-600 font-medium mb-1">TOEFL</p>
              <p className="text-2xl font-bold text-green-700">
                {formData.toefl || <span className="text-gray-300 text-lg">--</span>}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl border border-purple-100">
              <p className="text-xs text-purple-600 font-medium mb-1">GRE</p>
              <p className="text-2xl font-bold text-purple-700">
                {formData.gre || <span className="text-gray-300 text-lg">--</span>}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border border-stone-100 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900">Career Goals</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ViewField icon={Briefcase} label="Target Industry" value={getIndustryLabel(formData.industry)} />
            <ViewField icon={Globe} label="Work Location" value={getWorkLocationLabel(formData.workLocation)} />
            <ViewField icon={DollarSign} label="Budget" value={formData.budget} />
          </div>
          {formData.careerGoals && (
            <div className="mt-6 p-4 bg-stone-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-2">Career Aspirations</p>
              <p className="text-sm text-gray-700 leading-relaxed">{formData.careerGoals}</p>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Edit Personal Information</h3>
          <p className="text-sm text-gray-500">Update student profile details</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={saving} className="gap-2">
            <X className="w-4 h-4" />
            Cancel
          </Button>
          <Button type="submit" disabled={saving} className="gap-2 bg-purple-500 hover:bg-purple-600">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <Card className="p-6 border border-stone-100 shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-blue-50 rounded-lg">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <h4 className="font-semibold text-gray-900">Personal Details</h4>
          <Badge variant="outline" className="ml-2 text-xs">Required</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <EditField label="First Name" required error={errors.firstName}>
            <Input
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              className={errors.firstName ? 'border-red-300 focus:border-red-500' : ''}
              placeholder="Enter first name"
            />
          </EditField>
          <EditField label="Last Name" required error={errors.lastName}>
            <Input
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              className={errors.lastName ? 'border-red-300 focus:border-red-500' : ''}
              placeholder="Enter last name"
            />
          </EditField>
          <EditField label="Phone">
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+1 234 567 8900"
            />
          </EditField>
          <EditField label="Country">
            <Input
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              placeholder="Enter country"
            />
          </EditField>
          <EditField label="Age">
            <Input
              type="number"
              min="15"
              max="100"
              value={formData.age}
              onChange={(e) => handleChange('age', e.target.value)}
              placeholder="Enter age"
            />
          </EditField>
        </div>
      </Card>

      <Card className="p-6 border border-stone-100 shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-purple-50 rounded-lg">
            <GraduationCap className="w-5 h-5 text-purple-600" />
          </div>
          <h4 className="font-semibold text-gray-900">Academic Information</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <EditField label="Institution">
            <Input value={formData.institution} onChange={(e) => handleChange('institution', e.target.value)} placeholder="University name" />
          </EditField>
          <EditField label="Education Level">
            <Select value={formData.currentEducationLevel} onValueChange={(value) => handleChange('currentEducationLevel', value)}>
              <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
              <SelectContent>
                {EDUCATION_LEVELS.map(level => (
                  <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditField>
          <EditField label="Field of Study">
            <Input value={formData.fieldOfStudy} onChange={(e) => handleChange('fieldOfStudy', e.target.value)} placeholder="e.g., Computer Science" />
          </EditField>
          <EditField label="GPA">
            <Input value={formData.gpa} onChange={(e) => handleChange('gpa', e.target.value)} placeholder="e.g., 3.5/4.0" />
          </EditField>
          <EditField label="Graduation Year">
            <Input type="number" min="2000" max="2035" value={formData.graduationYear} onChange={(e) => handleChange('graduationYear', e.target.value)} placeholder="e.g., 2024" />
          </EditField>
          <EditField label="Preferred Degree Level">
            <Select value={formData.preferredDegreeLevel} onValueChange={(value) => handleChange('preferredDegreeLevel', value)}>
              <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
              <SelectContent>
                {EDUCATION_LEVELS.map(level => (
                  <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditField>
        </div>
      </Card>

      <Card className="p-6 border border-stone-100 shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-amber-50 rounded-lg">
            <FileText className="w-5 h-5 text-amber-600" />
          </div>
          <h4 className="font-semibold text-gray-900">Test Scores</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <EditField label="IELTS">
            <Input value={formData.ielts} onChange={(e) => handleChange('ielts', e.target.value)} placeholder="e.g., 7.5" />
          </EditField>
          <EditField label="TOEFL">
            <Input value={formData.toefl} onChange={(e) => handleChange('toefl', e.target.value)} placeholder="e.g., 100" />
          </EditField>
          <EditField label="GRE">
            <Input value={formData.gre} onChange={(e) => handleChange('gre', e.target.value)} placeholder="e.g., 320" />
          </EditField>
        </div>
      </Card>

      <Card className="p-6 border border-stone-100 shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-purple-50 rounded-lg">
            <Target className="w-5 h-5 text-purple-600" />
          </div>
          <h4 className="font-semibold text-gray-900">Career Goals</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <EditField label="Target Industry">
            <Select value={formData.industry} onValueChange={(value) => handleChange('industry', value)}>
              <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map(ind => (
                  <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditField>
          <EditField label="Preferred Work Location">
            <Select value={formData.workLocation} onValueChange={(value) => handleChange('workLocation', value)}>
              <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
              <SelectContent>
                {WORK_LOCATIONS.map(loc => (
                  <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditField>
          <EditField label="Budget">
            <Input value={formData.budget} onChange={(e) => handleChange('budget', e.target.value)} placeholder="e.g., $50,000 - $80,000" />
          </EditField>
        </div>
        <div className="mt-5">
          <EditField label="Career Aspirations">
            <Textarea value={formData.careerGoals} onChange={(e) => handleChange('careerGoals', e.target.value)} placeholder="Describe career goals and aspirations..." rows={4} className="resize-none" />
          </EditField>
        </div>
      </Card>
    </form>
  );
}
